import Helpers from "@/Helpers";

export type TestAnswerPdfDetailRow = {
    label: string;
    value: string;
};

export type TestAnswerPdfDetails = {
    rows: TestAnswerPdfDetailRow[];
};

export type TestAnswerPdfDetailsSource = {
    id?: number;
    firstName?: string;
    lastName?: string;
    status?: string;
    company?: string;
    customFields?: Array<{ name?: string; displayName?: string; fieldName?: string; value?: string }>;
    uniqueId?: string;
    language?: string;
    companyTestType?: { name?: string };
    testTypeName?: string;
    testTypeId?: number;
    location?: string;
    testInstances?: Array<{
        siteName?: string;
        companyJobName?: string;
        supervisor?: string;
    }>;
    createTime?: string;
    embargoTime?: string;
    codeExpiration?: string;
    welcomeTime?: string;
    startTime?: string;
    submitTime?: string;
    data1?: string;
    data2?: string;
};

type BuildTestAnswerPdfDetailsOptions = {
    isSystemAdmin: boolean;
    showSystemAdminFields: boolean;
};

function formatDetailDate(
    value: string | undefined,
    isSystemAdmin: boolean,
    dateOnly = false,
) {
    if (!value) return "";
    if (isSystemAdmin && !dateOnly) {
        return Helpers.EST(value);
    }
    return Helpers.EST(value, "MM/dd/yyyy");
}

function pushRow(rows: TestAnswerPdfDetailRow[], label: string, value: unknown) {
    const text = value == null ? "" : String(value).trim();
    if (!text) return;
    rows.push({ label, value: text });
}

export function buildTestAnswerPdfDetails(
    test: TestAnswerPdfDetailsSource,
    options: BuildTestAnswerPdfDetailsOptions,
): TestAnswerPdfDetails {
    const rows: TestAnswerPdfDetailRow[] = [];
    const { isSystemAdmin, showSystemAdminFields } = options;
    const singleInstance = Array.isArray(test.testInstances) && test.testInstances.length === 1
        ? test.testInstances[0]
        : null;

    if (showSystemAdminFields) {
        pushRow(rows, "Id", test.id);
        pushRow(rows, "Status", test.status);
    }

    pushRow(rows, "Company", test.company);

    if (Array.isArray(test.customFields)) {
        for (const field of test.customFields) {
            pushRow(rows, field.displayName || field.fieldName || field.name || "Field", field.value);
        }
    }

    pushRow(rows, "UniqueId", test.uniqueId);
    pushRow(rows, "Language", test.language);

    if (showSystemAdminFields && test.companyTestType?.name) {
        pushRow(
            rows,
            "Test Type",
            `${test.companyTestType.name} (${test.testTypeName} : ${test.testTypeId})`,
        );
    } else {
        pushRow(rows, "Test Type", test.companyTestType?.name);
    }

    pushRow(rows, "Location", test.location);

    if (singleInstance) {
        pushRow(rows, "Site", singleInstance.siteName);
        pushRow(rows, "Job", singleInstance.companyJobName);
        pushRow(rows, "Supervisor", singleInstance.supervisor);
    }

    pushRow(rows, "Created", formatDetailDate(test.createTime, isSystemAdmin, true));

    if (test.embargoTime) {
        pushRow(rows, "Embargo Date", formatDetailDate(test.embargoTime, isSystemAdmin, true));
    }

    if (test.welcomeTime) {
        pushRow(rows, "Welcome Time", formatDetailDate(test.welcomeTime, isSystemAdmin));
    }

    if (test.startTime) {
        pushRow(rows, "Start Time", formatDetailDate(test.startTime, isSystemAdmin));
    } else if (test.embargoTime) {
        pushRow(rows, "Embargo Time", formatDetailDate(test.embargoTime, isSystemAdmin));
    }

    if (test.submitTime) {
        pushRow(rows, "Submit Time", formatDetailDate(test.submitTime, isSystemAdmin));
    }

    pushRow(rows, "Data1", test.data1);
    pushRow(rows, "Data2", test.data2);

    return { rows };
}

function sanitizePdfFilenamePart(value: string): string {
    return value
        .replace(/[\\/:*?"<>|]/g, "")
        .replace(/\s+/g, " ")
        .trim();
}

function formatPdfStartDate(value: string | undefined): string {
    if (!value) return "";
    return Helpers.EST(value, "yyyy-MM-dd");
}

export function buildTestAnswerPdfFilename(test: TestAnswerPdfDetailsSource): string {
    const startDate = formatPdfStartDate(test.startTime || test.createTime) || "UnknownDate";
    const companyName = sanitizePdfFilenamePart(String(test.company || "")) || "UnknownCompany";
    const testId = test.id ?? "";
    const firstName = sanitizePdfFilenamePart(String(test.firstName || ""));
    const lastName = sanitizePdfFilenamePart(String(test.lastName || ""));
    const namePart = [firstName, lastName].filter(Boolean).join(" ");

    let base = `${startDate} ${companyName}`;
    if (namePart) {
        base += ` - ${namePart}`;
    }
    if (testId !== "") {
        base += ` (${testId})`;
    }

    base = sanitizePdfFilenamePart(base);
    if (!base) {
        return `answers_${testId || "test"}.pdf`;
    }

    return `${base}.pdf`;
}
